import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../store";
import ProfileLoader from "../../components/ABOProfile/ProfileLoader/ProfileLoader";
import LeafLinkButton from "../../common/leaf/LeafLinkButton/LeafLinkButton";
import { addNoteClickTrack, editNoteClickTrack } from "../../common/tealium/CustomerProfile/Note/NoteTrack";
import {
  aboProfileAPI,
  useCreateProfileNoteMutation,
  useDeleteProfileNoteMutation,
  useGetProfileNotesQuery,
  useUpdateProfileNoteMutation,
} from "../../services/AboProfile/AboProfileAPI";
import defaultContent from "../../content/profile";
import ViewNote from "./ViewNote/ViewNote";
import EditNote from "./EditNote/EditNote";
import "./Note.scss";

const Note = () => {
  const { t } = useTranslation(["profile"]);

  const editButtonText = `${t(
    "customerProfile.note.editButton",
    defaultContent["customerProfile"]["note"]["editButton"],
  )}`;
  const saveButtonText = `${t(
    "customerProfile.note.saveButton",
    defaultContent["customerProfile"]["note"]["saveButton"],
  )}`;
  const addButtonText = `${t(
    "customerProfile.note.addButton",
    defaultContent["customerProfile"]["note"]["addButton"],
  )}`;

  const dispatch = useAppDispatch();

  const affAbo = useAppSelector((state) => state.profile.affAbo);
  const { showNote = false, disableImpersonationNoteSave = false } =
    useAppSelector(({ boot }) => boot?.configuration) || {};
  const isImpersonation = useAppSelector(({ boot }) => boot.isImpersonation);
  const noteSaveDisabled = isImpersonation === true && disableImpersonationNoteSave === true;

  const { isFetching, data } = useGetProfileNotesQuery({ affabo: affAbo }, { skip: !affAbo });
  const { noteId = "", content = "" } = data?.notes?.[0] || {};

  const [updateNote] = useUpdateProfileNoteMutation();
  const [createNote] = useCreateProfileNoteMutation();
  const [deleteNote] = useDeleteProfileNoteMutation();

  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updatedNote, setUpdatedNote] = useState("");

  const _isFetching = showNote === true && isFetching === true;

  // Dispatch an optimistic update to the profile-notes query cache.
  const updateProfileNotesQuery = (updatedNoteId: string, updatedContent: string) => {
    dispatch(
      aboProfileAPI.util.updateQueryData("getProfileNotes", { affabo: affAbo }, (draft) => {
        if (!updatedNoteId) {
          draft.notes = [];
          return;
        }

        const currentNote = draft.notes[0];
        draft.notes = [
          {
            noteId: updatedNoteId,
            affAbo: currentNote?.affAbo ?? affAbo,
            content: updatedContent,
            contentFormat: currentNote?.contentFormat,
            createdAt: currentNote?.createdAt ?? Date.now(),
            lastModified: currentNote?.lastModified ?? Date.now(),
          },
        ];
      }),
    );
  };

  const createNewNote = async () => {
    if (noteSaveDisabled) return;
    if (!noteId && updatedNote) {
      updateProfileNotesQuery("temp", updatedNote);

      const result = await createNote({
        affabo: affAbo,
        content: updatedNote,
      }).unwrap();

      if (result) {
        updateProfileNotesQuery(result.noteId, updatedNote);
      }
    }
  };

  const updateNoteContent = async () => {
    if (noteSaveDisabled) return;
    if (noteId && updatedNote) {
      updateProfileNotesQuery(noteId, updatedNote);
      await updateNote({
        noteId,
        content: updatedNote,
      }).unwrap();
    }
  };

  const deleteNoteContent = async () => {
    if (noteSaveDisabled) return;
    if (noteId && updatedNote.length === 0) {
      updateProfileNotesQuery("", updatedNote);
      await deleteNote({ noteId }).unwrap();
    }
  };

  const addNoteButtonOnClick = () => {
    if (noteSaveDisabled) return;
    setIsEditing(true);
    addNoteClickTrack();
  };

  const editButtonOnClick = () => {
    if (noteSaveDisabled) return;
    setIsEditing(true);
    setUpdatedNote(content);
    editNoteClickTrack();
  };

  const saveNoteButtonOnClick = async () => {
    if (noteSaveDisabled) return;

    try {
      setIsUpdating(true);
      setIsEditing(false);
      await createNewNote();
      await updateNoteContent();
      await deleteNoteContent();
      setIsUpdating(false);
    } catch (error) {
      console.error();
    }
  };

  const noteOnChange = (note: string) => {
    setUpdatedNote(note);
  };

  if (_isFetching === false) {
    return (
      <div className="note note__container">
        <div className="note__container__content">
          <div className={`note__title${noteId || isEditing ? "" : "--hidden"}`}>
            <h2 className="note__title__text">{`${t(
              "customerProfile.note.pageTitle",
              defaultContent["customerProfile"]["note"]["pageTitle"],
            )}`}</h2>
            <LeafLinkButton
              as="link"
              onClick={editButtonOnClick}
              label={editButtonText}
              theme="blue"
              fontWeight="bold"
              fontSize="medium"
              show={isEditing === false}
              disabled={isUpdating === true || noteSaveDisabled}
            >
              {editButtonText}
            </LeafLinkButton>
            <LeafLinkButton
              as="link"
              onClick={saveNoteButtonOnClick}
              label={saveButtonText}
              theme="blue"
              fontWeight="bold"
              fontSize="medium"
              show={isEditing === true}
              disabled={isUpdating === true || noteSaveDisabled}
            >
              {saveButtonText}
            </LeafLinkButton>
          </div>
          <div className={`note__container__content__viewNote${isEditing ? "--hidden" : ""}`}>
            <ViewNote noteId={noteId} content={content} />
          </div>
          <EditNote value={content} noteOnChange={noteOnChange} show={isEditing} />
        </div>
        <div className="note__container__content-btn">
          <LeafLinkButton
            onClick={addNoteButtonOnClick}
            label={addButtonText}
            as="button"
            fontWeight="bold"
            fontSize="medium"
            show={noteId === "" && isEditing === false}
            disabled={isUpdating === true || noteSaveDisabled}
          >
            {addButtonText}
          </LeafLinkButton>
        </div>
      </div>
    );
  }

  return (
    <div className="note__container__loader">
      <ProfileLoader row={1} card={true} isFetching={isFetching} />
    </div>
  );
};

export default Note;
